import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#00695c';
const PRIMARY_MID = '#00897b';
const PRIMARY_LT  = '#e0f2f1';

const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtV = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const StockValuation = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const history = useHistory();

    const today = new Date();
    const [toDate, setToDate]           = useState(fmt2Date(today));
    const [categories, setCategories]   = useState([]);
    const [categoryId, setCategoryId]   = useState('0');
    const [data, setData]               = useState([]);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState('');
    const [fetched, setFetched]         = useState(false);
    const [search, setSearch]           = useState('');

    useEffect(() => {
        if (!companyId || !token) return;
        fetchData('CategoryApi', `/${companyId}`, token)
            .then(res => setCategories(res?.response_data || res || []))
            .catch(() => setCategories([]));
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && toDate) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token, categoryId]);

    const fetchReport = async () => {
        if (!toDate) { setError('Please select a date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'InventoryReportApi',
                `/StockValuation/${toDate}/${companyId}/${branchId}/${categoryId || 0}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch stock valuation');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.Name || '').toLowerCase().includes(q) ||
            (r.Code || '').toLowerCase().includes(q) ||
            (r.CategoryName || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        ClosingQty:  acc.ClosingQty  + (parseFloat(r.ClosingQty)  || 0),
        StockValue:  acc.StockValue  + (parseFloat(r.StockValue)  || 0),
    }), { ClosingQty: 0, StockValue: 0 }), [filtered]);

    const handleExport = async () => {
        if (!filtered.length) return;
        const headers = ['#', 'Code', 'Item Name', 'Category', 'Closing Qty', 'Avg Cost', 'Stock Value'];
        const rows = filtered.map((r, i) => [
            i + 1, r.Code, r.Name, r.CategoryName,
            r.ClosingQty, r.AvgCost, r.StockValue,
        ]);

        await exportStyledExcel({
            reportTitle: 'STOCK VALUATION REPORT',
            sheetName: 'Stock Valuation',
            fileName: `StockValuation_${toDate}.xlsx`,
            companyName,
            username,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', 'Totals', fmtQ(totals.ClosingQty), '', fmtQ(totals.StockValue)],
            columnWidths: [4, 10, 28, 18, 12, 14, 16],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #4db6ac', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading stock valuation..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Stock Valuation
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Inventory Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <SearchableDropdown
                        value={categoryId}
                        onChange={v => setCategoryId(v)}
                        options={[{ value: '0', label: 'All Categories' }, ...categories.map(c => ({ value: String(c.Id), label: c.Name }))]}
                        placeholder="All Categories"
                        minWidth={160}
                        primaryColor={PRIMARY}
                    />
                    <input placeholder="Search item / category..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}>As On</span>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total Items',   value: filtered.length,              color: PRIMARY },
                            { label: 'Closing Qty',   value: fmtQ(totals.ClosingQty),      color: '#0277bd' },
                            { label: 'Stock Value',   value: fmtV(totals.StockValue),       color: '#059669' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        As on {toDate} &nbsp;|&nbsp; {filtered.length} items
                        {search && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 750, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 220 }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Category</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Closing Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Avg Cost</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#004d40' }}>Stock Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => (
                                <tr key={`${r.Id}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                    <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                    <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>
                                        <span style={{ color: '#64748b', fontSize: 10, marginRight: 4 }}>{r.Code}</span>{r.Name || '-'}
                                    </td>
                                    <td style={{ ...tdBase, color: '#64748b' }}>{r.CategoryName || '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 600 }}>{fmtQ(r.ClosingQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#374151' }}>{fmtV(r.AvgCost)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 700 }}>{fmtV(r.StockValue)}</td>
                                </tr>
                            ))}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700 }}>
                                    <td colSpan={3} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(totals.ClosingQty)}</td>
                                    <td style={{ ...tdBase }} />
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 700 }}>{fmtV(totals.StockValue)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StockValuation;
